"use client"

import * as React from "react"
import {
  ArrowDownLeftIcon,
  ArrowUpRightIcon,
  BanknoteIcon,
  LandmarkIcon,
  LayersIcon,
  ScaleIcon,
  SmartphoneIcon,
  WalletIcon,
  type LucideIcon,
} from "lucide-react"
import { Link } from "react-router-dom"

import { Skeleton, cn } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { LoadingRegion, SkeletonCard, SkeletonLine } from "@/shared/components/skeletons"
import { AliveLeadCell, AliveMark } from "@/shared/components/alive-cells"
import { formatMoney, sumMoney } from "@/features/finance/lib/finance-utils"
import { AccountingAccountType } from "@/features/finance/model/accounting-reports-types"
import { FinanceAccountType } from "@/features/finance/model/finance-types"

/** What a row is, for its mark and its section: a financial account's own type, else its ledger type. */
export enum AccountRowKind {
  Bank = "bank",
  Cash = "cash",
  MobileMoney = "mobileMoney",
  Asset = "asset",
  Liability = "liability",
  NetAsset = "netAsset",
  Income = "income",
  Expense = "expense",
}

export const FINANCIAL_ROW_KIND: Record<FinanceAccountType, AccountRowKind> = {
  [FinanceAccountType.Bank]: AccountRowKind.Bank,
  [FinanceAccountType.Cash]: AccountRowKind.Cash,
  [FinanceAccountType.MobileMoney]: AccountRowKind.MobileMoney,
}

export const LEDGER_ROW_KIND: Record<AccountingAccountType, AccountRowKind> = {
  [AccountingAccountType.Asset]: AccountRowKind.Asset,
  [AccountingAccountType.Liability]: AccountRowKind.Liability,
  [AccountingAccountType.NetAsset]: AccountRowKind.NetAsset,
  [AccountingAccountType.Income]: AccountRowKind.Income,
  [AccountingAccountType.Expense]: AccountRowKind.Expense,
}

const ROW_KIND_ICON: Record<AccountRowKind, LucideIcon> = {
  [AccountRowKind.Bank]: LandmarkIcon,
  [AccountRowKind.Cash]: BanknoteIcon,
  [AccountRowKind.MobileMoney]: SmartphoneIcon,
  [AccountRowKind.Asset]: WalletIcon,
  [AccountRowKind.Liability]: ScaleIcon,
  [AccountRowKind.NetAsset]: LayersIcon,
  [AccountRowKind.Income]: ArrowDownLeftIcon,
  [AccountRowKind.Expense]: ArrowUpRightIcon,
}

/*
 * One soft tint per family on the mark, so a row reads at a glance: what the church holds (sky), owes
 * (violet), its net assets (indigo), money in (teal), money out (rose). No orange (the logo's) and no plain
 * green (the app's actions), the same rule as the alive initials.
 */
const SKY_TINT = "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300"
const ROW_KIND_TINT: Record<AccountRowKind, string> = {
  [AccountRowKind.Bank]: SKY_TINT,
  [AccountRowKind.Cash]: SKY_TINT,
  [AccountRowKind.MobileMoney]: SKY_TINT,
  [AccountRowKind.Asset]: SKY_TINT,
  [AccountRowKind.Liability]: "bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300",
  [AccountRowKind.NetAsset]: "bg-indigo-100 text-indigo-800 dark:bg-indigo-500/15 dark:text-indigo-300",
  [AccountRowKind.Income]: "bg-teal-100 text-teal-800 dark:bg-teal-500/15 dark:text-teal-300",
  [AccountRowKind.Expense]: "bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300",
}

/** The ledger sections, in the chart's own order (1 assets, 2 liabilities, 3 net assets, 4 income, 5 expenses). */
const LEDGER_SECTIONS: { kind: AccountRowKind; type: AccountingAccountType }[] = [
  { kind: AccountRowKind.Asset, type: AccountingAccountType.Asset },
  { kind: AccountRowKind.Liability, type: AccountingAccountType.Liability },
  { kind: AccountRowKind.NetAsset, type: AccountingAccountType.NetAsset },
  { kind: AccountRowKind.Income, type: AccountingAccountType.Income },
  { kind: AccountRowKind.Expense, type: AccountingAccountType.Expense },
]

export type AccountRow = {
  id: string
  code: string | null
  name: string
  kind: AccountRowKind
  /** The financial account's own detail page; only financial rows have one. */
  detailPath: string | null
  typeLabel: string
  balance: string
  /** The accounting account behind this row, which drives the investigation link. Null when it never posted. */
  accountingAccountId: string | null
  /** Last 4 digits of the real account number (financial accounts only), shown masked under the name. */
  accountNumberLast4: string | null
}

/** A financial account's number shown masked, e.g. "6789" to "**** **** 6789". */
export function maskAccountNumber(last4: string): string {
  return `**** **** ${last4}`
}

/** A decimal string with no non-zero digit ("0", "0.00", "-0.00"). Needs no currency and cannot throw. */
function isZeroBalance(balance: string): boolean {
  return !/[1-9]/.test(balance)
}

export function AccountRowMark({ kind, className }: { kind: AccountRowKind; className?: string }) {
  return <AliveMark icon={ROW_KIND_ICON[kind]} className={cn(ROW_KIND_TINT[kind], className)} />
}

/**
 * The balance, the drill-in to the investigation Explorer (dotted underline). An empty account steps back so
 * the ones holding money stand out; one that never posted has no accounting account yet, so plain text.
 */
function AccountBalance({
  row,
  currency,
  onInvestigate,
  className,
}: {
  row: AccountRow
  currency: string
  onInvestigate: (row: AccountRow) => void
  className?: string
}) {
  const amountClassName = cn(
    "font-extrabold tabular-nums whitespace-nowrap",
    isZeroBalance(row.balance) && "text-muted-foreground font-semibold",
    className,
  )
  const amount = formatMoney(row.balance, currency)
  return row.accountingAccountId ? (
    <button
      type='button'
      className={cn(amountClassName, "underline decoration-dotted underline-offset-4 hover:decoration-solid")}
      onClick={() => onInvestigate(row)}
    >
      {amount}
    </button>
  ) : (
    <span className={amountClassName}>{amount}</span>
  )
}

/** A section's title with its count on the left and its total on the right. */
function SectionHeader({
  title,
  count,
  total,
  className,
}: {
  title: string
  count: number
  total: string
  className?: string
}) {
  const t = useTranslations("finance.accountsChart")
  return (
    <header className={cn("flex items-center justify-between gap-3", className)}>
      <span className='flex min-w-0 items-baseline gap-2'>
        <h3 className='truncate text-[13.5px] font-extrabold'>{title}</h3>
        <span className='text-muted-foreground shrink-0 text-xs font-semibold'>{t(count === 1 ? "accountCount.one" : "accountCount.other", { count })}</span>
      </span>
      <span className='shrink-0 text-[13px] tabular-nums'>
        <span className='text-muted-foreground font-semibold'>{t("total")}</span>{" "}
        <span className='font-extrabold'>{total}</span>
      </span>
    </header>
  )
}

/**
 * One ledger section: a soft card, its header, and compact rows (mark, name, mono code, balance). On wide
 * screens the rows flow into newspaper columns, top to bottom like a printed chart, so a row of three values
 * never stretches across the whole page.
 */
function LedgerSection({
  title,
  rows,
  currency,
  onInvestigate,
}: {
  title: string
  rows: AccountRow[]
  currency: string
  onInvestigate: (row: AccountRow) => void
}) {
  const total = sumMoney(rows.map((row) => row.balance), currency)
  return (
    <section aria-label={title} className='bg-card overflow-hidden rounded-2xl border'>
      <SectionHeader title={title} count={rows.length} total={formatMoney(total, currency)} className='bg-muted/30 border-b px-4 py-2.5' />
      <ul
        className={cn(
          "gap-0 [column-rule:1px_solid_var(--color-border)]",
          rows.length > 6 && "xl:columns-2",
          rows.length > 12 && "2xl:columns-3",
        )}
      >
        {rows.map((row) => (
          <li
            key={row.id}
            className='border-border/60 hover:bg-muted/40 flex break-inside-avoid items-center justify-between gap-4 border-b px-4 py-2'
          >
            <AliveLeadCell
              leading={<AccountRowMark kind={row.kind} />}
              title={row.name}
              subtitle={row.code ? <span className='font-mono tabular-nums'>{row.code}</span> : null}
            />
            <AccountBalance row={row} currency={currency} onInvestigate={onInvestigate} className='text-[13px]' />
          </li>
        ))}
      </ul>
    </section>
  )
}

/** A financial account as a card: few of them, and each one is about its balance. */
function FinancialAccountCard({
  row,
  currency,
  href,
  onInvestigate,
}: {
  row: AccountRow
  currency: string
  href: string | null
  onInvestigate: (row: AccountRow) => void
}) {
  const detail = [row.typeLabel, row.accountNumberLast4 ? maskAccountNumber(row.accountNumberLast4) : null]
    .filter(Boolean)
    .join(" · ")
  return (
    <li className='bg-card flex min-w-0 flex-col gap-3 rounded-2xl border p-4'>
      <span className='flex min-w-0 items-center gap-2.5'>
        <AccountRowMark kind={row.kind} />
        <span className='flex min-w-0 flex-col'>
          {href ? (
            <Link
              to={href}
              className='truncate text-[13.5px] font-extrabold hover:underline focus-visible:underline focus-visible:outline-none'
            >
              {row.name}
            </Link>
          ) : (
            <span className='truncate text-[13.5px] font-extrabold'>{row.name}</span>
          )}
          <span className='text-muted-foreground truncate text-xs font-semibold'>{detail}</span>
        </span>
      </span>
      <span className='flex items-end justify-between gap-2'>
        <AccountBalance row={row} currency={currency} onInvestigate={onInvestigate} className='text-xl leading-tight' />
        {row.code ? <span className='text-muted-foreground font-mono text-xs tabular-nums'>{row.code}</span> : null}
      </span>
    </li>
  )
}

/** One ledger section while the chart loads: its tinted header, then compact rows (mark, name, code, balance). */
function LedgerSectionSkeleton({ rows }: { rows: number }) {
  return (
    <SkeletonCard className='overflow-hidden'>
      <div className='bg-muted/30 flex items-center justify-between gap-3 border-b px-4 py-2.5'>
        <span className='flex items-center gap-2'>
          <SkeletonLine className='h-3.5 w-28' />
          <SkeletonLine className='w-16' />
        </span>
        <SkeletonLine className='w-24' />
      </div>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className='border-border/60 flex items-center gap-3 border-b px-4 py-2'>
          <Skeleton className='size-8 shrink-0 rounded-full' />
          <span className='flex min-w-0 flex-1 flex-col gap-1.5'>
            <SkeletonLine className={index % 2 === 0 ? "w-1/3" : "w-1/4"} />
            <SkeletonLine className='h-2.5 w-12' />
          </span>
          <SkeletonLine className='w-20' />
        </div>
      ))}
    </SkeletonCard>
  )
}

/** The chart while it loads: the ledger sections in their real cards (the financial tab shows its card grid). */
function BoardSkeleton({ financial }: { financial: boolean }) {
  if (financial) {
    return (
      <LoadingRegion className='flex flex-col gap-3'>
        <span className='flex items-center justify-between px-1'>
          <SkeletonLine className='h-3.5 w-40' />
          <SkeletonLine className='w-24' />
        </span>
        <div className='grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4'>
          {Array.from({ length: 4 }, (_, index) => (
            <SkeletonCard key={index} className='flex flex-col gap-3 p-4'>
              <span className='flex items-center gap-2.5'>
                <Skeleton className='size-8 shrink-0 rounded-full' />
                <span className='flex flex-1 flex-col gap-1.5'>
                  <SkeletonLine className='h-3.5 w-3/5' />
                  <SkeletonLine className='w-2/5' />
                </span>
              </span>
              <Skeleton className='h-6 w-32 rounded-lg' />
            </SkeletonCard>
          ))}
        </div>
      </LoadingRegion>
    )
  }
  return (
    <LoadingRegion className='flex flex-col gap-4'>
      <LedgerSectionSkeleton rows={4} />
      <LedgerSectionSkeleton rows={3} />
    </LoadingRegion>
  )
}

/**
 * The Chart of Accounts on desktop, in place of a wide three-column table: the financial accounts as a card
 * grid (balance first), the ledger as one section per type with its count and total. Inside a single-type tab
 * the section's own title already says the type, so no row repeats it.
 */
export function ChartAccountsBoard({
  rows,
  financial,
  currency,
  isInitialLoading,
  emptyState,
  detailHref,
  onInvestigate,
}: {
  rows: AccountRow[]
  /** The Financial tab: bank, cash and mobile money accounts, drawn as cards. */
  financial: boolean
  currency: string
  isInitialLoading: boolean
  emptyState: React.ReactNode
  detailHref: (row: AccountRow) => string | null
  onInvestigate: (row: AccountRow) => void
}) {
  const t = useTranslations("finance.accountsChart")
  if (isInitialLoading) return <BoardSkeleton financial={Boolean(financial)} />
  if (rows.length === 0) return <div className='bg-card rounded-2xl border'>{emptyState}</div>

  if (financial) {
    const total = sumMoney(rows.map((row) => row.balance), currency)
    return (
      <div className='flex flex-col gap-3'>
        <SectionHeader title={t("sections.FINANCIAL")} count={rows.length} total={formatMoney(total, currency)} className='px-1' />
        <ul className='grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4'>
          {rows.map((row) => (
            <FinancialAccountCard key={row.id} row={row} currency={currency} href={detailHref(row)} onInvestigate={onInvestigate} />
          ))}
        </ul>
      </div>
    )
  }

  return (
    <div className='flex flex-col gap-4'>
      {LEDGER_SECTIONS.map(({ kind, type }) => {
        const sectionRows = rows.filter((row) => row.kind === kind)
        if (sectionRows.length === 0) return null
        return (
          <LedgerSection
            key={kind}
            title={t(`sections.${type}` as never)}
            rows={sectionRows}
            currency={currency}
            onInvestigate={onInvestigate}
          />
        )
      })}
    </div>
  )
}
